import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, Validators, FormControl, FormArray } from '@angular/forms';
import { InfractionService } from '@/services/infraction/infraction.service';
import { SeverityType, ReporterType, EvidenceType } from '@/interfaces/infractions';
import { Option } from '@/shared/select-list/select-list.component';
import { dateMustBeBeforeOrEqualThanToday } from '@/validators/dateMustBeBeforeOrEqualThanToday';
import { normalize } from '@/utils/normalizeString';
import { mustHaveOneRecord } from '@/validators/mustHaveOneRecord';
import { SelectOption } from '@/shared/select/select.component.interface';
import { Observable, combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-form-detail',
  templateUrl: './form-detail.component.html',
  styleUrls: ['./form-detail.component.css'],
})
export class FormDetailComponent implements OnInit {
  @Input() formNumber: number = 3;
  @Input() controlForm: FormGroup = new FormGroup({
    day: new FormControl('', [Validators.required, dateMustBeBeforeOrEqualThanToday()]),
    hour: new FormControl(''), // now is optional
    personResponsibleId: new FormControl(0), // now is optional
    severityTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
    infrigmentTypeId: new FormControl(0, [Validators.required, Validators.min(1)]),
    observation: new FormControl(''),
    reporterTypeId: new FormControl(0), // now is optional
    reporterPersonId: new FormControl(0, [Validators.required, Validators.min(0)]),
    evidenceTypes: new FormControl([]),
    witnesses: new FormControl([]),
  });

  @Output() emmitSeverityName = new EventEmitter();
  @Output() emmitInfrigmentTypeName = new EventEmitter();

  // Escucha los cambios en assetId
  @Input()
  get assetId(): number {
    return this._assetId;
  }
  set assetId(assetId: number) {
    if (assetId) {
      this._assetId = assetId;
      this.getSeverityTypes(assetId);
      this.getReporterTypes(assetId);
      this.getEvidenceTypes(assetId);
    }
    this.controlForm.reset();
    this._controlEvidenceTypes.reset();
    this.controlForm.controls.witnesses.patchValue([0]);
    this.controlForm.controls.reporterTypeId.patchValue(0);
    this.controlForm.controls.reporterPersonId.patchValue(0);
    this.controlForm.controls.infrigmentTypeId.patchValue('');
    this.shouldShowWitnesForm = false;
    this.shouldShowReporterPersonForm = false;
  }
  private _assetId: number = 0;

  get controlFormDay() {
    return this.controlForm.controls.day as FormControl;
  }
  get dayError(): string {
    const error = this.controlForm.controls.day?.errors;
    if (error) return error.description;
    return '';
  }
  get controlFormPersonResponsibleId() {
    return this.controlForm.controls.personResponsibleId as FormControl;
  }
  get controlFormReporterPersonId() {
    return this.controlForm.controls.reporterPersonId as FormControl;
  }
  get controlFormInfrigmentTypeId() {
    return this.controlForm.controls.infrigmentTypeId as FormControl;
  }
  get controlFormWitnesses() {
    return this.controlForm.controls.witnesses as FormArray;
  }
  get controlFormObservation() {
    return this.controlForm.controls.observation as FormControl;
  }
  get controlFormEvidenceTypes() {
    return this._controlEvidenceTypes as FormArray;
  }

  // Custom-mirrors form controls
  private _controlEvidenceTypes = new FormArray([]);
  public controlWitnesses = new FormArray([
    new FormControl(0),
    new FormControl(0),
    new FormControl(0),
  ]);

  controlFormEvidenceTypesControl(index: number) {
    return this._controlEvidenceTypes.controls[index] as FormControl;
  }

  // reporterPersonId = new FormControl(0);

  // End custom-mirrors form controls

  // Form controls here are not used in the ui
  // We are using it for better control of change on they values
  severityTypes: FormControl = new FormControl([]); // <SeverityType[]>
  infrigmentTypes: FormControl = new FormControl([]); // SelectOption[]
  reporterTypes: FormControl = new FormControl([]); // ReporterType[]

  evidenceTypes: EvidenceType[] = [];

  shouldShowWitnesForm: boolean = false;
  shouldShowReporterPersonForm: boolean = false;

  constructor(
    private infractionService: InfractionService,
  ) {
    let controlsLength = 0;
    this._controlEvidenceTypes.valueChanges.subscribe({
      next: (vectorOfBooleans: boolean[]) => {
        const vectorOfIndexes = vectorOfBooleans.reduce<number[]>((outVector, value, index) => {
          if (value) outVector.push(index);
          return outVector;
        }, []);

        const shouldShowWitnesForm = vectorOfIndexes.some(
          (index) => normalize(this.evidenceTypes[index].name) === normalize('TESTIGO'),
        );
        if (!this.shouldShowWitnesForm && shouldShowWitnesForm) {
          // this.controlForm.controls.witnesses.patchValue([]);
        }
        else if (this.shouldShowWitnesForm && !shouldShowWitnesForm) {
          this.controlWitnesses.reset();
          this.controlForm.controls.witnesses.patchValue([0]);
        }

        this.shouldShowWitnesForm = shouldShowWitnesForm;

        const evidenceTypesIdSelected = vectorOfIndexes.map((index) => this.evidenceTypes[index].id);
        if (controlsLength === vectorOfBooleans.length) {
          this.controlForm.controls.evidenceTypes.patchValue(evidenceTypesIdSelected);
        }
        else {
          controlsLength = vectorOfBooleans.length;
        }
      },
    });

    this.controlWitnesses.valueChanges.subscribe({
      next: (dirtyVectorOfIds: number[]) => {
        const vectorOfIds = dirtyVectorOfIds.filter((value) => !!value);
        this.controlForm.controls.witnesses.patchValue(vectorOfIds);
      },
    });
  }

  ngOnInit(): void {
    // When severityId change
    this.controlForm.controls.severityTypeId.valueChanges.subscribe({
      next: (value) => {
        if (value) {
          this.controlFormInfrigmentTypeId.reset();
          this.infractionService.getInfrigmentTypes(this._assetId, value).subscribe({
            next: (infrigmentTypes) => {
              this.infrigmentTypes.setValue(
                infrigmentTypes.map((infrigmentType): SelectOption => ({ id: infrigmentType.id, value: infrigmentType.name })),
              );
            },
          });
          if (this.severityTypes.value.length > 0) {
            const severityType = this.severityTypes.value.find((type: SeverityType) => type.id === value);
            if (severityType) {
              this.emmitSeverityName.emit(severityType.name);
            }
          }
          else {
            this.severityTypes.valueChanges.pipe(
              filter((arr) => arr.length > 0),
            ).subscribe({
              next: (severityTypes) => {
                const severityType = severityTypes.find((type: SeverityType) => type.id === value);
                if (severityType) {
                  this.emmitSeverityName.emit(severityType.name);
                }
              },
            });
          }
        }
      },
    });

    // When witnesses change
    this.controlForm.controls.witnesses.valueChanges.subscribe({
      next: (values: number[]) => {
        // on reset, reset the local control too
        if (values === null) {
          this.controlWitnesses.reset();
        }
        else {
          const mirrorValue = (this.controlWitnesses.value as number[]).filter((value) => !!value);
          const valuesAreEquivalent = values.every((id) => mirrorValue.find((mirrorId) => mirrorId === id));
          if (!valuesAreEquivalent) {
            const distinctsIds = [...new Set([...values, ...mirrorValue])];
            for (let index = 0; index < distinctsIds.length; index++) {
              const formControl = this.controlWitnesses.controls[index];
              formControl.setValue(distinctsIds[index]);
            }
          }
        }
      },
    });

    // When reporterTypeId change
    this.controlForm.controls.reporterTypeId.valueChanges.subscribe({
      next: (selectedId) => {
        if (this.reporterTypes.value.length > 0) {
          const reporterType = this.reporterTypes.value.find(({ id }: ReporterType) => id === selectedId);
          if (reporterType) {
            if (normalize(reporterType.name) === normalize('Tercero')) {
              this.shouldShowReporterPersonForm = true;
              this.controlForm.controls.reporterPersonId.reset();
            }
            else {
              this.shouldShowReporterPersonForm = false;
              this.controlForm.controls.reporterPersonId.patchValue(0);
              // this.reporterPersonId.reset();
            }
          }
        }
        else {
          this.reporterTypes.valueChanges.pipe(
            filter((arr) => arr.length > 0),
          ).subscribe({
            next: (reporterTypes: ReporterType[]) => {
              const reporterType = reporterTypes.find(({ id }) => id === selectedId);
              if (reporterType) {
                if (normalize(reporterType.name) === normalize('Tercero')) {
                  this.shouldShowReporterPersonForm = true;
                  // this.controlForm.controls.reporterPersonId.reset();
                }
                else {
                  this.shouldShowReporterPersonForm = false;
                  this.controlForm.controls.reporterPersonId.patchValue(0);
                  // this.reporterPersonId.reset();
                }
              }
            },
          });
        }

      },
    });

    this.controlForm.controls.infrigmentTypeId.valueChanges.subscribe({
      next: (id) => {
        if (this.infrigmentTypes.value.length > 0) {
          const infrigmentType = this.infrigmentTypes.value.find((option: SelectOption) => +option.id === +id);
          if (infrigmentType) {
            this.emmitInfrigmentTypeName.emit(infrigmentType.value);
          }
        }
        else {
          this.infrigmentTypes.valueChanges.pipe(
            filter((arr) => arr.length > 0),
          ).subscribe({
            next: (infrigmentTypes) => {
              const infrigmentType = infrigmentTypes.find((option: SelectOption) => +option.id === +id);
              if (infrigmentType) {
                this.emmitInfrigmentTypeName.emit(infrigmentType.value);
              }
            },
          });
        }
      },
    });
    const evidenceTypes$ = this.controlForm.controls.evidenceTypes.valueChanges;
    const evidenceTypesGetted$ = this.evidenceTypesGetted.valueChanges;

    let defaultValueSetted = false;
    combineLatest(evidenceTypes$, evidenceTypesGetted$).subscribe({
      next: ([vectorOfIds]: [number[], boolean]) => {
        if (vectorOfIds && vectorOfIds.length > 0 && !defaultValueSetted) {
          const vectorOfSelectedIndex = this.evidenceTypes.map(({ id }) => !!vectorOfIds.find((selectedId) => +id === +selectedId));
          defaultValueSetted = true;

          const evidenceTypesControl = this._controlEvidenceTypes as FormArray;
          vectorOfSelectedIndex.map((isSelected, index) => {
            if (isSelected) {
              evidenceTypesControl.at(index).setValue(true);
            }
          });
        }
      },
    });

    evidenceTypesGetted$.pipe(take(1)).subscribe({
      next: () => {
        this.controlForm.controls.witnesses.clearValidators();
        this.controlForm.controls.witnesses.updateValueAndValidity();
      },
    });

    evidenceTypes$.subscribe({
      next: (vectorOfIds: number[]) => {
        const witnessType = this.evidenceTypes.find(({ name }) => normalize(name) === normalize('testigo'));
        if (witnessType) {
          if (vectorOfIds.some((id) => +id === +witnessType.id)) {
            this.controlForm.controls.witnesses.setValidators([mustHaveOneRecord('Debe seleccionar al menos un testigo.')]);
            this.controlForm.controls.witnesses.updateValueAndValidity();
          }
          else {
            this.controlForm.controls.witnesses.clearValidators();
            this.controlForm.controls.witnesses.updateValueAndValidity();
          }
        }
      },
    });

  }

  getSeverityTypes(assetId: number) {
    this.infractionService.getSeverityTypes(assetId).subscribe({
      next: (severityTypes) => {
        this.severityTypes.setValue(severityTypes);
      },
    });
  }

  getReporterTypes(assetId: number) {
    this.infractionService.getReportersTypes(assetId).subscribe({
      next: (reporterTypes) => {
        this.reporterTypes.setValue(reporterTypes);
      },
    });
  }

  evidenceTypesGetted = new FormControl();

  getEvidenceTypes(assetId: number) {
    this.infractionService.getEvidenceTypes(assetId).subscribe({
      next: (evidenceTypes) => {
        this.evidenceTypes = evidenceTypes;
        const evidenceTypesControl = this._controlEvidenceTypes as FormArray;
        for (let index = evidenceTypesControl.controls.length - 1; index >= 0; index--) {
          evidenceTypesControl.removeAt(index);
        }
        evidenceTypes.map(() => {
          evidenceTypesControl.push(new FormControl(false));
        });
        this.evidenceTypesGetted.setValue(true);
      },
    });
  }

}
